<script lang="ts" setup>
import SancareAccordionItem from '@sancare/typescript-commons/src/design-system/molecules/SancareAccordionItem.vue'
import SancareListItem from '@sancare/typescript-commons/src/design-system/molecules/SancareListItem.vue'
import {
  getDrugEventPrestationTypeLabel,
  getDrugEventPrestationTypes,
} from '@sancare/typescript-commons/src/misc/DrugEventHelper'
import { DrugEventPrestationType, HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { HealthDataType, TextualHealthEntryType } from '@sancare/typescript-commons/src/types/health-data'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { Report } from '@/stay-displayer/health-data/types'
import type { SelectedChunks } from '@/stay-displayer/types'
import { StayHealthData } from '@/store/modules/stay/types.js'

import {
  documentCategories,
  getDocumentCategoryLabel,
  getHealthDataLabel,
  healthDataLabels,
} from './health-data/documentLabels'

export interface ReportSelectorProps {
  healthData: StayHealthData
  selectedChunks: SelectedChunks | null
  mainContent: HealthDataDocument | null
  isLoading?: boolean
}

const { t } = useI18n()

const props = withDefaults(defineProps<ReportSelectorProps>(), {
  isLoading: false,
})

const emit = defineEmits<{
  (e: 'setMainContent', payload: HealthDataDocument): void
}>()

const healthDataTypes = Object.keys(healthDataLabels)
const textualSourceTypes = ['medicalHistory', 'nurseReport', 'targetNurseReport', 'labResultComment', 'surgeryLabel']

const highlightedCategories = computed(() => {
  if (!props.selectedChunks) {
    return {}
  }
  // We extract the highlighted items from selectedChunks (see chunkJustifications.ts::getSelectedChunks)
  let highlightedCategories = {
    reports: Object.keys(props.selectedChunks?.reports).filter((id) => Boolean(props.selectedChunks?.reports[id])).map((id) => Number(id)),
    [HealthDataType.LAB_RESULT]: Object.values(props.selectedChunks?.labResults ?? {}).some(Boolean)
      || Object.values(props.selectedChunks?.categoricalLabResults ?? {}).some(Boolean),
    [HealthDataType.HEALTH_CONSTANT]: Object.values(props.selectedChunks?.healthConstants ?? {}).some(Boolean),
  }
  textualSourceTypes.forEach((sourceType) => {
    highlightedCategories[sourceType] = props.healthData[sourceType]
      .some((item) => Boolean(props.selectedChunks?.textualHealthEntries?.[item.id]))
  })

  highlightedCategories = { ...highlightedCategories, ...{ drugEvent: getHighlightedDrugEventCategories() } }

  return highlightedCategories
})

const healthDataCounts = computed(() => {
  const counts = {
    ...HealthDataType,
    ...TextualHealthEntryType,
  } as Record<string, string | number>

  Object.values(counts).forEach((dataType) => {
    if (props.healthData[dataType]) {
      counts[dataType] = props.healthData[dataType].length
    } else if (HealthDataType.HEALTH_CONSTANT === dataType) {
      counts[dataType] = props.healthData.healthConstants.length
    } else if (HealthDataType.ANNOTATION === dataType) {
      counts[dataType] = props.healthData.annotations.length
    } else if (HealthDataType.LAB_RESULT === dataType) {
      counts[dataType] = props.healthData.labResults.length + props.healthData.categoricalLabResults.length
    }
  })

  return counts
})

const presentCategories = computed(() => healthDataTypes.filter((healthDataType) => healthDataCounts.value[healthDataType]))

const drugEventPrestationTypes = computed(() => getDrugEventPrestationTypes(props.healthData.drugEvents))

const drugEventCounts = computed(() => {
  const counts: Record<string, number> = {}
  drugEventPrestationTypes.value.forEach((type) => {
    const typeFinal = type === DrugEventPrestationType.NONE ? null : type
    counts[type] = props.healthData.drugEvents.filter((drugEvent) => drugEvent.prestationType === typeFinal).length
  })
  return counts
})

const groupedDocumentsCount = computed(() => drugEventPrestationTypes.value.length + presentCategories.value.length)

function getHighlightedDrugEventCategories() {
  if (!props.selectedChunks?.drugEvents) {
    return null
  }
  const highlightedCategories = {}
  const highlightedDrugEventsIds = Object
    .keys(props.selectedChunks.drugEvents).filter((id) => props.selectedChunks.drugEvents[id] === true)
    .map((id) => Number(id))
  const highlightedDrugEvents = props.healthData.drugEvents.filter((drugEvent) => highlightedDrugEventsIds.includes(drugEvent.id))

  Object.values(DrugEventPrestationType).forEach((type) => {
    highlightedCategories[type] = highlightedDrugEvents.some((drugEvent) => {
      const typeFinal = type === DrugEventPrestationType.NONE ? null : type
      return drugEvent.prestationType === typeFinal
    })
  })

  return highlightedCategories
}

function handleHealthDataSelection(mainContent: HealthDataDocument) {
  emit('setMainContent', mainContent)
}

function displayedReportDescription(report) {
  return report.documentTitle ? report.documentTitle : '[ Sans Titre ]'
}

function sortedReports() {
  const documentTemplates = new Set(Object.keys(documentCategories))
  const sortedReports: Record<string, Report[]> = {}

  props.healthData.reports.forEach((report) => {
    if (report.documentTemplate && documentTemplates.has(report.documentTemplate)) {
      if (!sortedReports[report.documentTemplate]) {
        sortedReports[report.documentTemplate] = []
      }
      sortedReports[report.documentTemplate].push(report)
      return
    }
    if (!sortedReports['Documents']) {
      sortedReports['Documents'] = []
    }
    sortedReports['Documents'].push(report)
  })
  return sortedReports
}
</script>

<template>
  <h2 class="document-title mt-4">
    {{ t('stayDisplayer.mco.documents') }}
  </h2>
  <div class="d-flex flex-column gap-2 mb-3">
    <sancare-accordion-item
      v-for="(reports, category) in sortedReports()"
      :key="category"
      :title="isLoading ? `${getDocumentCategoryLabel(category)} :` : `${getDocumentCategoryLabel(category)} (${reports.length}) :`"
      :is-collapsible="true"
      variant="flush"
    >
      <sancare-list-item
        v-for="report in reports"
        :key="report.id"
        :is-clickable="true"
        :class="{
          active: mainContent?.type === HealthDataType.REPORT && report.id === mainContent.targetId,
          highlighted: highlightedCategories.reports && highlightedCategories.reports.includes(report.id),
        }"
        @click="handleHealthDataSelection({ type: 'report', targetId: report.id })"
      >
        {{ displayedReportDescription(report) }}
      </sancare-list-item>
    </sancare-accordion-item>
    <sancare-accordion-item
      v-if="presentCategories.length || drugEventPrestationTypes.length"
      :title="isLoading ? 'Documents regroupés :' : `Documents regroupés (${groupedDocumentsCount}) :`"
      :is-collapsible="true"
      variant="flush"
    >
      <sancare-list-item
        v-for="type in drugEventPrestationTypes"
        :key="type"
        :is-clickable="true"
        :class="{
          active: mainContent?.type === HealthDataType.DRUG_EVENT && mainContent.drugEventPrestationType === type,
          highlighted: Boolean(highlightedCategories?.drugEvent?.[type]),
        }"
        @click="handleHealthDataSelection({ type: 'drugEvent', targetId: null, drugEventPrestationType: type })"
      >
        {{ getDrugEventPrestationTypeLabel(type) }}
        <template v-if="!isLoading">
          ({{ drugEventCounts[type] }})
        </template>
      </sancare-list-item>

      <sancare-list-item
        v-for="healthDataType in presentCategories"
        :key="healthDataType"
        :is-clickable="true"
        :class="{
          active: mainContent?.type === healthDataType,
          highlighted: highlightedCategories[healthDataType],
        }"
        @click="handleHealthDataSelection({ type: healthDataType, targetId: null })"
      >
        {{ getHealthDataLabel(healthDataType) }}
        <template v-if="!isLoading">
          ({{ healthDataCounts[healthDataType] }})
        </template>
      </sancare-list-item>
    </sancare-accordion-item>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';

:deep(.sancare-list-item.highlighted) {
  background: #e4f0ff;
  color: #0889eb;
  font-weight: bold;
}
</style>
